/**
 * Enhanced Attachment Handler
 * Integrates intelligent file analysis with Gemini API for optimal performance
 * 
 * Features:
 * - Pre-analysis of attachments before sending to main AI
 * - Context-aware prompting based on file content
 * - Parallel processing of multiple attachments
 * - Smart caching to avoid redundant analysis
 * - Optimized token usage
 */

import { 
    analyzeFileIntelligently, 
    analyzeMultipleFiles,
    FileAnalysisResult,
    extractTextContext,
    getQuickFilePreview
} from './intelligentFileAnalyzer';
import { Attachment } from '../types';

export interface EnhancedAttachmentContext {
    attachments: Attachment[];
    analyses: FileAnalysisResult[];
    contextPrompt: string;
    estimatedTokens: number;
    processingTime: number;
}

/**
 * Process attachments with intelligent pre-analysis
 */
export async function processAttachmentsIntelligently(
    attachments: Attachment[],
    userQuestion: string,
    apiKey?: string
): Promise<EnhancedAttachmentContext> {
    const startTime = performance.now();
    
    if (attachments.length === 0) {
        return {
            attachments: [],
            analyses: [],
            contextPrompt: '',
            estimatedTokens: 0,
            processingTime: 0
        };
    }
    
    console.log(`[AttachmentHandler] Processing ${attachments.length} attachment(s)`);
    
    // Prepare files for analysis
    const files = attachments.map(att => ({
        data: att.data,
        mimeType: att.mimeType,
        fileName: att.name
    }));
    
    // Analyze all files in parallel
    const analyses = await analyzeMultipleFiles(files, userQuestion, apiKey);
    
    // Build context-aware prompt
    const contextPrompt = buildContextPrompt(attachments, analyses, userQuestion);
    
    // Estimate token usage
    const estimatedTokens = estimateTokenUsage(attachments, analyses);
    
    const processingTime = performance.now() - startTime;
    
    console.log(`[AttachmentHandler] Processed ${attachments.length} files in ${processingTime.toFixed(0)}ms`);
    console.log(`[AttachmentHandler] Estimated tokens: ${estimatedTokens}`);
    
    return {
        attachments,
        analyses,
        contextPrompt,
        estimatedTokens,
        processingTime
    };
}

/**
 * Build optimized context prompt based on file analyses
 */
function buildContextPrompt(
    attachments: Attachment[],
    analyses: FileAnalysisResult[],
    userQuestion: string
): string {
    if (analyses.length === 0) return '';
    
    let prompt = '[ATTACHMENT CONTEXT]\n';
    
    // Add file summaries
    analyses.forEach((analysis, idx) => {
        const attachment = attachments[idx];
        prompt += `\nFile ${idx + 1}: ${attachment.name}\n`;
        prompt += `Type: ${analysis.detectedType}\n`;
        prompt += `Summary: ${analysis.summary}\n`;
        
        if (analysis.keyEntities.length > 0) {
            prompt += `Key Topics: ${analysis.keyEntities.join(', ')}\n`;
        }
    });
    
    // Add user question context
    prompt += `\nUser Question: ${userQuestion}\n`;
    prompt += '\n[INSTRUCTIONS]\n';
    prompt += 'Analyze the attached file(s) in the context of the user\'s question. ';
    prompt += 'Reference specific details from the files in your response. ';
    prompt += 'Be accurate and concise.\n';
    
    return prompt;
}

/**
 * Estimate token usage for attachments
 */
function estimateTokenUsage(
    attachments: Attachment[],
    analyses: FileAnalysisResult[]
): number {
    let tokens = 0;
    
    // Base tokens for each attachment (Gemini's vision processing)
    attachments.forEach((att, idx) => {
        const sizeKB = att.data.length / 1024;
        
        if (att.mimeType.startsWith('image/')) {
            // Images: ~258 tokens per 1024x1024 tile
            // Estimate based on file size (rough approximation)
            tokens += Math.ceil(sizeKB / 100) * 258;
        } else if (att.mimeType === 'application/pdf') {
            // PDFs: estimate based on size
            tokens += Math.ceil(sizeKB / 10) * 100;
        }
    });
    
    // Add tokens for context prompt
    const contextTokens = analyses.reduce((sum, analysis) => {
        return sum + Math.ceil(analysis.summary.length / 4); // ~4 chars per token
    }, 0);
    
    tokens += contextTokens;
    
    return tokens;
}

/**
 * Create optimized prompt for Gemini based on attachment analysis
 */
export function createOptimizedAttachmentPrompt(
    userMessage: string,
    context: EnhancedAttachmentContext,
    mode: 'fast' | 'accurate' = 'accurate'
): string {
    if (context.analyses.length === 0) {
        return userMessage;
    }
    
    let prompt = '';
    
    // Add mode-specific instructions
    if (mode === 'fast') {
        prompt += '[FAST MODE: Provide concise, direct answers]\n\n';
    } else {
        prompt += '[ACCURATE MODE: Provide detailed, verified answers]\n\n';
    }
    
    // Add file context
    prompt += context.contextPrompt;
    
    // Add user message
    prompt += `\nUser Request: ${userMessage}\n`;
    
    return prompt;
}

/**
 * Get attachment preview text for UI
 */
export function getAttachmentPreview(attachment: Attachment, analysis?: FileAnalysisResult): string {
    if (analysis) {
        return `${attachment.name} - ${analysis.summary.substring(0, 100)}${analysis.summary.length > 100 ? '...' : ''}`;
    }
    return getQuickFilePreview(attachment.mimeType, attachment.name, attachment.data.length);
}

/**
 * Check if attachments should be pre-analyzed
 */
export function shouldPreAnalyze(
    attachments: Attachment[],
    userQuestion: string
): boolean {
    // Pre-analyze if:
    // 1. Multiple attachments (to understand relationships)
    // 2. Complex question (needs context)
    // 3. Large files (to optimize processing)
    
    if (attachments.length > 1) return true;
    
    const complexityIndicators = [
        /compare/i,
        /difference/i,
        /relationship/i,
        /analyze/i,
        /explain.*detail/i,
        /summarize/i
    ];
    
    if (complexityIndicators.some(pattern => pattern.test(userQuestion))) {
        return true;
    }
    
    // Check file size
    const hasLargeFile = attachments.some(att => att.data.length > 500000);
    if (hasLargeFile) return true;
    
    return false;
}

/**
 * Extract suggested follow-up questions from analyses
 */
export function extractSuggestedQuestions(analyses: FileAnalysisResult[]): string[] {
    const questions: string[] = [];
    
    analyses.forEach(analysis => {
        questions.push(...analysis.suggestedQuestions);
    });
    
    // Remove duplicates and limit to top 5
    return [...new Set(questions)].slice(0, 5);
}

/**
 * Merge multiple file analyses into a single context
 */
export function mergeFileAnalyses(analyses: FileAnalysisResult[]): {
    combinedSummary: string;
    allKeyEntities: string[];
    overallConfidence: number;
} {
    if (analyses.length === 0) {
        return {
            combinedSummary: '',
            allKeyEntities: [],
            overallConfidence: 0
        };
    }
    
    if (analyses.length === 1) {
        return {
            combinedSummary: analyses[0].summary,
            allKeyEntities: analyses[0].keyEntities,
            overallConfidence: analyses[0].confidence
        };
    }
    
    // Combine summaries
    const combinedSummary = analyses
        .map((a, idx) => `File ${idx + 1}: ${a.summary}`)
        .join(' | ');
    
    // Merge key entities (remove duplicates)
    const allKeyEntities = [...new Set(
        analyses.flatMap(a => a.keyEntities)
    )];
    
    // Average confidence
    const overallConfidence = Math.round(
        analyses.reduce((sum, a) => sum + a.confidence, 0) / analyses.length
    );
    
    return {
        combinedSummary,
        allKeyEntities,
        overallConfidence
    };
}
